import { useEffect, useState } from 'react';

import { format, isValid } from 'date-fns';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import type { User } from '@/models/Users';
import type { Subscriptions } from '@/models/Subscription';

// A missing or malformed create_at would otherwise make format() throw and take the whole page down.
const formatCreateDate = (value?: Date | string) => {
	const date = new Date(value!);
	return isValid(date) ? format(date, 'yyyy-MM-dd') : '';
};

const ProfileComponent = () => {
	const baseLink = globalThis.origin + '/invitation/';
	const [inscriptionLink, setInscriptionLink] = useState('');

	let appContext = { isLogged: false, user: ' ', email: ' ', role: 'USER' };
	appContext = JSON.parse(localStorage.getItem('appContext')!);

	// Client details - Subscriptions
	const [clientDetails, setClientDetails] = useState<User>({
		id: 0,
		create_at: new Date(),
		email: '',
		passwrd: '',
		firstname: '',
		lastname: '',
		phone: '',
		address: '',
		status: '',
		role: '',
		subscription_by: '',
		link_id: '',
		subscription_count: 0,
	});

	// Subscriptions
	const [subscriptions, setSubscriptions] = useState<Subscriptions[]>([]);
	const fetchSubscriptions = async (email: string) => {
		try {
			const response = await fetch('/api/subscriptions/user/' + email);
			const data = await response.json();
			setSubscriptions(data);
		} catch (error) {
			console.error('Failed to fetch Subscriptions:', error);
		}
	};

	// Invitations
	const [invitations, setInvitations] = useState<User[]>([]);
	const fetchInvitations = async (email: string) => {
		try {
			const response = await fetch('/api/clients/subscriptions_by/' + email);
			const data = await response.json();
			setInvitations(data);
		} catch (error) {
			console.error('Failed to fetch Invitations:', error);
		}
	};

	useEffect(() => {
		const fetchClientDetails = async (email: string) => {
			const response = await fetch('/api/users/email/' + email);
			const user = await response.json();
			setClientDetails(user);
			fetchSubscriptions(email);
			fetchInvitations(email);
			setInscriptionLink(baseLink + user.link_id);
		};
		fetchClientDetails(appContext.email);
	}, []);

	return (
		<div className="min-h-screen" style={{ backgroundColor: '#e6e6b8' }}>
			<div className="top-0 z-10000 w-full px-0 py-3 sm:p-6">
				<Navbar />
			</div>

			<div className="container mx-auto py-6">
				<div className="text-center font-bold text-2xl sm:text-3xl">الملف الشخصي</div>
			</div>

			<div className="container mx-auto flex flex-col gap-3 py-6 sm:py-8">
				<div className="text-center text-lg text-green-800 sm:text-xl">رابط التسجيل عن طريق الدعوة</div>
				{/* The invitation URL is one long unbreakable token; without this it
				    pushed the page wider than the phone screen. */}
				<div className="break-all text-center text-sm sm:text-xl">
					<a href={inscriptionLink} target="_blank">
						{inscriptionLink}
					</a>
				</div>
			</div>

			<div className="container">
				<div className="flex flex-col gap-4 sm:flex-row">
					<div className="w-full min-w-0 space-y-2">
						<Label htmlFor="firstname" className="font-bold">
							الإسم
						</Label>
						<Input
							id="firstname"
							type="text"
							placeholder=""
							className="text-right"
							style={{ borderColor: 'green' }}
							value={clientDetails?.firstname}
							readOnly
						/>
					</div>
					<div className="w-full min-w-0 space-y-2">
						<Label htmlFor="lastname" className="font-bold">
							اللقب
						</Label>
						<Input
							id="lastname"
							type="text"
							placeholder=""
							className="text-right"
							style={{ borderColor: 'green' }}
							value={clientDetails?.lastname}
							readOnly
						/>
					</div>
				</div>

				<div className="flex flex-col gap-4 sm:flex-row">
					<div className="w-full min-w-0 space-y-2">
						<Label htmlFor="email" className="font-bold">
							البريد الإلكتروني
						</Label>
						<Input
							id="email"
							type="text"
							placeholder=""
							className="text-right"
							style={{ borderColor: 'green' }}
							value={clientDetails?.email}
							readOnly
						/>
					</div>
					<div className="w-full min-w-0 space-y-2">
						<Label htmlFor="phone" className="font-bold">
							رقم الهاتف
						</Label>
						<Input
							id="phone"
							type="text"
							placeholder=""
							className="text-right"
							style={{ borderColor: 'green' }}
							value={clientDetails?.phone}
							readOnly
						/>
					</div>
				</div>

				<div className="flex flex-col gap-4 sm:flex-row">
					<div className="w-full min-w-0 space-y-2">
						<Label htmlFor="createDate" className="font-bold">
							تاريخ التسجيل
						</Label>
						<Input
							id="createDate"
							type="text"
							placeholder=""
							className="text-right"
							style={{ borderColor: 'green' }}
							value={formatCreateDate(clientDetails?.create_at)}
							readOnly
						/>
					</div>
					<div className="w-full min-w-0 space-y-2">
						<Label htmlFor="lastname" className="font-bold">
							مسجل من طرف
						</Label>
						<Input
							id="lastname"
							type="text"
							placeholder=""
							className="text-right"
							style={{ borderColor: 'green' }}
							value={clientDetails?.subscription_by}
							readOnly
						/>
					</div>
				</div>

				<div className="p-4"></div>
				<div dir="rtl">
					<Tabs defaultValue="invitations" className="w-full" dir="rtl">
						<TabsList className="h-auto w-full flex-wrap justify-start">
							<TabsTrigger value="invitations">المسجلون عن طريق الدعوة</TabsTrigger>
							<TabsTrigger value="subscriptions">الاشتراكات</TabsTrigger>
						</TabsList>
						<TabsContent value="invitations">
							<div className="overflow-x-auto rounded-sm" style={{ borderWidth: 1, borderColor: 'green' }} dir="rtl">
								<Table dir="rtl" className="min-w-[620px]">
									<TableHeader>
										<TableRow>
											<TableHead className="font-bold text-foreground text-center">الرقم</TableHead>
											<TableHead className="font-bold text-foreground text-center">تاريخ التسجيل</TableHead>
											<TableHead className="font-bold text-foreground text-center">الإسم واللقب</TableHead>
											<TableHead className="font-bold text-foreground text-center">البريد الإلكتروني</TableHead>
											<TableHead className="font-bold text-foreground text-center">رقم الهاتف</TableHead>
											<TableHead className="font-bold text-foreground text-center">عدد التابعون</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{invitations?.map((invitation, index) => (
											<TableRow key={invitation.id}>
												<TableCell className="text-center">{index + 1}</TableCell>
												<TableCell className="text-center">{format(new Date(invitation.create_at), 'yyyy-MM-dd')}</TableCell>
												<TableCell className="text-center">{invitation.lastname + ' ' + invitation.firstname}</TableCell>
												<TableCell className="text-center">{invitation.email}</TableCell>
												<TableCell className="text-center">{invitation.phone}</TableCell>
												<TableCell className="text-center">{invitation.subscription_count}</TableCell>
											</TableRow>
										))}
									</TableBody>
									<TableFooter></TableFooter>
								</Table>
							</div>
						</TabsContent>
						<TabsContent value="subscriptions">
							<div className="overflow-x-auto rounded-sm" style={{ borderWidth: 1, borderColor: 'green' }} dir="rtl">
								<Table dir="rtl" className="min-w-[320px]">
									<TableHeader>
										<TableRow>
											<TableHead className="font-bold text-foreground text-center">الرقم</TableHead>
											<TableHead className="font-bold text-foreground text-center">تاريخ التسجيل</TableHead>
											<TableHead className="font-bold text-foreground text-center">المبلغ</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{subscriptions?.map((subscription, index) => (
											<TableRow key={subscription.id}>
												<TableCell className="text-center">{index + 1}</TableCell>
												<TableCell className="text-center">{format(new Date(subscription.create_at), 'yyyy-MM-dd')}</TableCell>
												<TableCell className="text-center">{subscription.amount}</TableCell>
											</TableRow>
										))}
									</TableBody>
									<TableFooter></TableFooter>
								</Table>
							</div>
						</TabsContent>
					</Tabs>
				</div>
			</div>

			<Footer />
		</div>
	);
};

export default ProfileComponent;
